import React, { useState, useEffect } from 'react';
import { ViewState } from '../types';

interface HeaderProps {
  onNavigate: (view: ViewState) => void;
  currentView: ViewState;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSubscribeClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onNavigate, currentView, searchQuery, onSearchChange, onSubscribeClick }) => {
  const [internalQuery, setInternalQuery] = useState(searchQuery);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setInternalQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalQuery(value);
    onSearchChange(value);
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ease-in-out ${scrolled ? 'py-3' : 'py-4'}`} role="banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`relative flex justify-between items-center h-[3.75rem] sm:h-14 px-2.5 sm:px-5 border transition-all duration-300 ${scrolled
            ? 'border-white/[0.08] bg-deep-void/85 rounded-2xl backdrop-blur-xl shadow-[0_16px_40px_-22px_rgba(0,0,0,0.8)]'
            : 'border-white/[0.06] bg-mid-night-blue/40 rounded-2xl backdrop-blur-md'
            }`}
        >
          <a
            href="/"
            className="flex items-center cursor-pointer flex-shrink-0 group"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              onNavigate('home');
            }}
            aria-label="Go to Noxicon Home"
          >
            <div className="bg-transparent p-0 mr-2 sm:mr-3 group-hover:scale-110 transition-transform duration-500 ease-out text-cyan-accent">
              <svg className="w-7 h-7 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1-8.313-12.454z" />
                <path d="M17 3.5a.5.5 0 0 1 .5.5v1.5h1.5a.5.5 0 0 1 0 1h-1.5v1.5a.5.5 0 0 1-1 0V6.5h-1.5a.5.5 0 0 1 0-1h1.5V4a.5.5 0 0 1 .5-.5z" />
              </svg>
            </div>
            <span className="text-2xl font-bold text-starlight-mist tracking-[0.2em] hidden sm:block font-serif uppercase">Noxicon</span>
          </a>

          <div className="flex-grow min-w-0 max-w-md mx-1.5 sm:mx-4 md:mx-10 opacity-85 focus-within:opacity-100 transition-opacity">
            <div className="relative group">
              <label htmlFor="search-input" className="sr-only">Search Input</label>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-slate-400 group-focus-within:text-cyan-accent transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                id="search-input"
                type="search"
                value={internalQuery}
                onChange={handleChange}
                placeholder="Search..."
                className="block w-full h-9 sm:h-10 pl-9 pr-3 border border-white/[0.12] rounded-full bg-deep-void/60 appearance-none text-xs sm:text-sm leading-tight text-starlight-mist placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-accent focus:border-cyan-accent/60 focus:bg-deep-void/80 transition-all backdrop-blur-sm"
              />
              {internalQuery && (
                <button
                  onClick={() => {
                    setInternalQuery('');
                    onSearchChange('');
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300"
                  aria-label="Clear search"
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <nav className="flex items-center space-x-1.5 sm:space-x-6 flex-shrink-0" aria-label="Main Navigation">
            <button
              onClick={onSubscribeClick}
              aria-label="Subscribe to latest posts"
              className="group relative px-2.5 sm:px-5 py-2 sm:py-2.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-widest whitespace-nowrap transition-all border border-cyan-accent/40 bg-cyan-accent/15 hover:bg-cyan-accent/25 hover:border-cyan-accent/70 hover:-translate-y-0.5"
            >
              <span className="relative text-starlight-mist flex items-center gap-1 sm:gap-2 leading-none transition-colors font-serif italic">
                <span className="hidden sm:inline">Subscribe</span>
                <span className="sm:hidden">Join</span>
              </span>
            </button>
          </nav>

          {currentView !== 'home' && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-accent/70 to-transparent" />
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
